import { gql } from "@apollo/client";

export const RESOLVE_EHR_MEMBER = gql`
  query ResolveEhrMember($sourceSystem: EhrSourceSystem!, $sourceSystemId: String!, $includeInactive: Boolean) {
    resolveEhrMember(sourceSystem: $sourceSystem, sourceSystemId: $sourceSystemId, includeInactive: $includeInactive) {
      _id
      name
      dob
      gender
      ipaCode
      astranaId
      healthplanName
      healthplanCode
      lineOfBusiness
      isActive
      enrollmentDate
      memberPolicyId
    }
  }
`;

/**
 * EHR source systems the app can resolve members from. Mirrors the backend
 * `EhrSourceSystem` GraphQL enum. Which EHRs the *extension* actually scrapes is
 * a separate, build-time concern (`VITE_ENABLED_EHR_ADAPTERS`); this type is the
 * set the web app knows how to label and query.
 */
export type EhrSourceSystem = "EMR_ELATION" | "EMR_ECW" | "EMR_ATHENA";

/** Known source-system ids, for validating an untrusted value off the wire. */
export const EHR_SOURCE_SYSTEMS: readonly EhrSourceSystem[] = [
  "EMR_ELATION",
  "EMR_ECW",
  "EMR_ATHENA",
];

const EHR_SOURCE_SYSTEM_LABELS: Record<EhrSourceSystem, string> = {
  EMR_ELATION: "Elation",
  EMR_ECW: "eClinicalWorks",
  EMR_ATHENA: "athenahealth",
};

/** Human-readable EHR name for user-facing copy (cards, banners). */
export function ehrSourceSystemLabel(sourceSystem: EhrSourceSystem): string {
  return EHR_SOURCE_SYSTEM_LABELS[sourceSystem] ?? "the EHR";
}

export interface EhrMemberMatch {
  _id: string;
  name?: string | null;
  dob?: string | null;
  gender?: string | null;
  ipaCode?: string | null;
  astranaId?: string | null;
  healthplanName?: string | null;
  healthplanCode?: string | null;
  lineOfBusiness?: string | null;
  isActive?: boolean | null;
  enrollmentDate?: string | null;
  memberPolicyId?: string | null;
}

/** Route prefix for connect-only fallback members rendered via the library's astranaId path. */
export const ASTRANA_NAV_PREFIX = "astrana:";

/**
 * A connect-only fallback match: a member valid in connect.patient_profile but
 * absent from Mongo. The gateway returns it with a synthetic
 * `pp:<ipaCode>:<memberPolicyId>` _id AND an `astranaId`. Member 360 renders
 * these via the library's EXISTING astranaId render path
 * (`<MemberProfile patient={{ astranaId }} />` — the header + all panels query
 * `connectPatient(astranaId)`), navigated as `/members/astrana:<astranaId>`.
 *
 * NOT the provider-portal `pp:` companyId/mpi path: that builds a patient with
 * no `astranaId`, so the library's astranaId path never activates.
 */
export function isConnectFallbackMatch(member: EhrMemberMatch): boolean {
  return member._id.startsWith("pp:") && !!member.astranaId;
}

/** Navigation id for a connect-only fallback match: `astrana:<astranaId>`. */
export function fallbackNavId(member: EhrMemberMatch): string {
  return `${ASTRANA_NAV_PREFIX}${member.astranaId ?? ""}`;
}

/**
 * Router state carried alongside the `astrana:` route so MemberProfilePage can
 * build `{ astranaId }` even if the id is unparseable (robustness) — mirrors how
 * the `ppMember` state is carried for the `pp:` path.
 */
export function astranaStateFromMatch(member: EhrMemberMatch): { astranaId: string } {
  return { astranaId: member.astranaId ?? "" };
}

/**
 * Recovers the astranaId for a connect-only member profile: prefers the carried
 * router state, else parses it from the `astrana:<astranaId>` route id (hard
 * refresh). Returns "" when `id` is not an `astrana:` id — the caller then
 * treats the route as a normal Mongo member (MEMBER_BY_ID lookup unchanged).
 */
export function astranaIdFromNav(
  id: string | undefined,
  state?: { astranaId?: string } | null,
): string {
  if (!id || !id.startsWith(ASTRANA_NAV_PREFIX)) return "";
  return state?.astranaId || id.slice(ASTRANA_NAV_PREFIX.length);
}

export interface ResolveEhrMemberVars {
  sourceSystem: EhrSourceSystem;
  sourceSystemId: string;
  includeInactive?: boolean;
}

export interface ResolveEhrMemberResult {
  resolveEhrMember: EhrMemberMatch[];
}
